import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { RefreshCw, RotateCcw } from "lucide-react";

type Stats = {
  pending: number;
  processing: number;
  done: number;
  failed: number;
  oldest_pending_seconds: number;
  total_attempts: number;
};

type FailedRow = {
  id: string;
  item_id: string;
  item_type: string;
  organization_id: string;
  attempts: number;
  last_error: string | null;
  scheduled_for: string;
  updated_at: string;
};

const StatCard = ({ label, value, tone }: { label: string; value: string | number; tone?: "warn" | "danger" | "ok" }) => {
  const toneClass =
    tone === "danger" ? "text-red-600" : tone === "warn" ? "text-amber-600" : tone === "ok" ? "text-emerald-700" : "text-foreground";
  return (
    <div className="bg-card rounded-[10px] shadow-md p-4">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">{label}</div>
      <div className={`text-2xl font-semibold ${toneClass}`}>{value}</div>
    </div>
  );
};

function formatDuration(seconds: number): string {
  if (!seconds || seconds < 1) return "—";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${Math.round(seconds / 3600)}h`;
}

export function AdminEmbedQueueTab() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [failed, setFailed] = useState<FailedRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [requeuing, setRequeuing] = useState<string | null>(null);
  const [bulkRequeuing, setBulkRequeuing] = useState(false);

  const load = useCallback(async () => {
    const [statsRes, failedRes] = await Promise.all([
      supabase.rpc("admin_embed_queue_stats"),
      supabase.rpc("admin_embed_queue_failed", { p_limit: 100 }),
    ]);
    if (statsRes.error) {
      toast.error(`Stats failed: ${statsRes.error.message}`);
    } else {
      const row = Array.isArray(statsRes.data) ? statsRes.data[0] : statsRes.data;
      setStats(row as Stats);
    }
    if (failedRes.error) {
      toast.error(`Failed list error: ${failedRes.error.message}`);
    } else {
      setFailed((failedRes.data as FailedRow[]) || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const i = setInterval(load, 30_000);
    return () => clearInterval(i);
  }, [load]);

  const requeueOne = async (id: string) => {
    setRequeuing(id);
    const { error } = await supabase.rpc("admin_embed_queue_requeue", { p_id: id });
    setRequeuing(null);
    if (error) toast.error(error.message);
    else {
      toast.success("Re-queued");
      load();
    }
  };

  const requeueAll = async () => {
    setBulkRequeuing(true);
    const { data, error } = await supabase.rpc("admin_embed_queue_requeue_all_failed");
    setBulkRequeuing(false);
    if (error) toast.error(error.message);
    else {
      toast.success(`Re-queued ${data ?? 0} job(s)`);
      load();
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Embed Queue</h1>
          <p className="text-sm text-muted-foreground">
            Durable re-indexing queue. Failed jobs have exhausted retries — re-queue once underlying cause is fixed.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
          <Button
            size="sm"
            onClick={requeueAll}
            disabled={bulkRequeuing || !stats?.failed}
          >
            <RotateCcw className="w-4 h-4 mr-1.5" />
            {bulkRequeuing ? "Re-queuing…" : `Re-queue all failed${stats?.failed ? ` (${stats.failed})` : ""}`}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {loading || !stats ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-card rounded-[10px] shadow-md p-4">
              <Skeleton className="h-3 w-20 mb-3" />
              <Skeleton className="h-7 w-16" />
            </div>
          ))
        ) : (
          <>
            <StatCard label="Pending" value={stats.pending} tone={stats.pending > 100 ? "warn" : undefined} />
            <StatCard label="Processing" value={stats.processing} />
            <StatCard label="Done" value={stats.done} tone="ok" />
            <StatCard label="Failed" value={stats.failed} tone={stats.failed > 0 ? "danger" : "ok"} />
            <StatCard label="Oldest pending" value={formatDuration(stats.oldest_pending_seconds)} tone={stats.oldest_pending_seconds > 600 ? "warn" : undefined} />
            <StatCard label="Total attempts" value={stats.total_attempts} />
          </>
        )}
      </div>

      <div className="bg-card rounded-[10px] shadow-md overflow-hidden">
        <div className="px-4 py-3 border-b">
          <h2 className="text-sm font-semibold">Failed jobs (most recent first)</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="text-left px-4 py-2">Type</th>
                <th className="text-left px-4 py-2">Item ID</th>
                <th className="text-left px-4 py-2">Attempts</th>
                <th className="text-left px-4 py-2">Last error</th>
                <th className="text-left px-4 py-2">Updated</th>
                <th className="text-right px-4 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading || failed === null ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    <td colSpan={6} className="px-4 py-3"><Skeleton className="h-4 w-full" /></td>
                  </tr>
                ))
              ) : failed.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                    No failed jobs. Queue is healthy.
                  </td>
                </tr>
              ) : (
                failed.map((r) => (
                  <tr key={r.id} className="border-t">
                    <td className="px-4 py-2 font-mono text-xs">{r.item_type}</td>
                    <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{r.item_id.slice(0, 8)}…</td>
                    <td className="px-4 py-2">{r.attempts}</td>
                    <td className="px-4 py-2 text-xs text-red-700 max-w-[420px] truncate" title={r.last_error || ""}>
                      {r.last_error || "—"}
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">
                      {new Date(r.updated_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => requeueOne(r.id)}
                        disabled={requeuing === r.id}
                      >
                        {requeuing === r.id ? "…" : "Re-queue"}
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default AdminEmbedQueueTab;
